import { isSpoken, planScene, type Scene, type Step, stepAt, type Timeline } from '@scripted/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { adminScenes } from '@/routes'

// "Play scene" in the console: the working copy, timed exactly as the
// trainer's Listen mode (shared planScene), on one browser AudioContext.
//
// Two modes: `continuous` runs the scene like the trainer; `line` plays one
// line and waits — for reviewing a scene and fixing lines as you go.
// Pausing and jumping reschedule the audio from a position on the scene's
// clock, so any line can be played, replayed or skipped.
//
// Clips stream: playing starts once the first line's audio is decoded, the
// rest download in scene order behind it, and each clip is scheduled shortly
// before it's due. If one isn't ready in time, playback holds its place and
// picks up from there when it arrives.
//
// Safari only lets an AudioContext run if it was resumed during a click or
// key press, and may refuse a resume() at any other time — so every
// user-initiated action unlocks first, nothing ever suspends the context,
// and if the clock stops anyway the player shows itself paused, so the next
// press resumes it.

export type PlayMode = 'line' | 'continuous'
type Playback = { scene: Scene; timeline: Timeline; audioUrls: Record<string, string> }
export type ScenePlayerState = {
  sceneId: number | null
  status: 'idle' | 'loading' | 'playing' | 'paused'
  current: (Step & { line: Scene['lines'][number] }) | null
  /** Line mode: the current line has finished and the player is waiting to go on. */
  finished: boolean
}

const LEAD_S = 0.1
/** How far ahead of the clock clips are handed to the AudioContext. */
const AHEAD_MS = 2000
const DOWNLOADS = 4
const MODE_KEY = 'scripted.scenePlayer.mode'

function storedMode(): PlayMode | null {
  try {
    const mode = localStorage.getItem(MODE_KEY)
    return mode === 'line' || mode === 'continuous' ? mode : null
  } catch {
    return null
  }
}

export function useScenePlayer(productionId: string, { defaultMode = 'line' }: { defaultMode?: PlayMode } = {}) {
  const ctx = useRef<AudioContext | null>(null)
  const buffers = useRef(new Map<string, AudioBuffer>())
  const downloads = useRef(new Map<string, Promise<AudioBuffer | null>>())
  /** Lines whose audio is scheduled since the last (re)schedule. */
  const scheduled = useRef(new Set<string>())
  /** Line mode: the one step being played. */
  const only = useRef<Step | null>(null)
  /** Audio is scheduled no earlier than this (where playback was started from). */
  const floor = useRef(0)
  /** Waiting on a clip that isn't ready: playback holds at `floor` until it arrives. */
  const buffering = useRef(false)
  /** Clips that failed to download or decode this load: skipped, not waited for. */
  const unplayable = useRef(new Set<string>())
  /** Clock ticks in a row with the AudioContext not running. */
  const stalled = useRef(0)
  /** `schedule`, for `fill` (defined before it) to restart from a hold. */
  const reschedule = useRef<(fromMs: number, line: Step | null) => void>(() => {})
  // Bumped by every (re)schedule, pause and stop, so a wait for a clip that
  // ends afterwards doesn't restart the clock.
  const epoch = useRef(0)
  const sources = useRef<AudioBufferSourceNode[]>([])
  const playback = useRef<Playback | null>(null)
  const origin = useRef(0)
  /** Where on the scene clock to stop (end of the line in line mode), or null to run to the end. */
  const until = useRef<number | null>(null)
  const pausedAt = useRef(0)
  /** The scene changed since it was loaded (an edit, a new take): reload before playing on. */
  const stale = useRef(false)
  // Bumped by every play/stop, so a play still loading when you stop or
  // start another doesn't schedule its audio afterwards.
  const run = useRef(0)
  const [mode, setModeState] = useState<PlayMode>(() => storedMode() ?? defaultMode)
  const modeRef = useRef(mode)
  const [state, setState] = useState<ScenePlayerState>({
    sceneId: null,
    status: 'idle',
    current: null,
    finished: false,
  })
  const current = useRef<Step | null>(null)

  const stopSources = useCallback(() => {
    for (const s of sources.current) {
      try {
        s.stop()
      } catch {
        // already ended
      }
    }
    sources.current = []
  }, [])

  const stop = useCallback(() => {
    run.current++
    epoch.current++
    stopSources()
    playback.current = null
    current.current = null
    setState({ sceneId: null, status: 'idle', current: null, finished: false })
  }, [stopSources])

  const position = useCallback(() => (ctx.current ? (ctx.current.currentTime - origin.current) * 1000 : 0), [])

  const show = useCallback((step: Step | null, patch: Partial<ScenePlayerState>) => {
    current.current = step
    const line = step && playback.current?.scene.lines[step.index]
    setState((s) => ({ ...s, current: step && line ? { ...step, line } : null, ...patch }))
  }, [])

  /** Downloads and decodes a clip once; null if it can't be had (tried again next time). */
  const download = useCallback((url: string) => {
    let pending = downloads.current.get(url)
    if (!pending) {
      pending = fetch(url)
        .then((r) => (r.ok ? r.arrayBuffer() : null))
        .then((data) => (data && ctx.current ? ctx.current.decodeAudioData(data) : null))
        .catch((error) => {
          console.warn('Scene player: could not decode', url, error)
          return null
        })
        .then((buffer) => {
          if (buffer) buffers.current.set(url, buffer)
          else downloads.current.delete(url)
          return buffer
        })
      downloads.current.set(url, pending)
    }
    return pending
  }, [])

  /** Fetches the scene's clips in playing order from `from`, a few at a time. */
  const prefetch = useCallback(
    (p: Playback, from: Step) => {
      const steps = p.timeline.steps
      const start = Math.max(0, steps.indexOf(from))
      const queue = [
        ...new Set([...steps.slice(start), ...steps.slice(0, start)].flatMap((s) => urlOf(p, s) ?? [])),
      ].filter((url) => !buffers.current.has(url))
      const worker = async () => {
        for (let url = queue.shift(); url; url = queue.shift()) await download(url)
      }
      for (let i = 0; i < DOWNLOADS; i++) void worker()
    },
    [download],
  )

  /** Hands the AudioContext every clip due within AHEAD_MS; holds playback for one that's late. */
  const fill = useCallback(() => {
    const audio = ctx.current
    const p = playback.current
    if (!audio || !p || buffering.current) return
    const at = Math.max(position(), floor.current)
    for (const step of only.current ? [only.current] : p.timeline.steps) {
      const url = urlOf(p, step)
      if (!url || scheduled.current.has(step.lineId) || unplayable.current.has(url)) continue
      if (step.startMs > at + AHEAD_MS || step.startMs + step.durationMs <= at) continue
      const buffer = buffers.current.get(url)
      if (!buffer) {
        if (step.startMs - at > LEAD_S * 1000) continue
        // Hold here (the clock runs on, so anything already scheduled is
        // stopped) and start again from this point once it's in.
        const holdAt = at
        const waitingFor = epoch.current
        stopSources()
        floor.current = holdAt
        buffering.current = true
        void download(url).then((ready) => {
          if (waitingFor !== epoch.current) return
          // Can't be had: skip it rather than wait forever.
          if (!ready) unplayable.current.add(url)
          reschedule.current(holdAt, only.current)
        })
        return
      }
      const startMs = Math.max(step.startMs, at)
      const source = audio.createBufferSource()
      source.buffer = buffer
      source.connect(audio.destination)
      source.start(origin.current + startMs / 1000, (startMs - step.startMs) / 1000)
      sources.current.push(source)
      scheduled.current.add(step.lineId)
    }
  }, [download, position, stopSources])

  /** Plays from `fromMs`: just `line`'s audio (line mode), or everything to the end. */
  const schedule = useCallback(
    (fromMs: number, line: Step | null) => {
      const audio = ctx.current
      if (!audio || !playback.current) return
      stopSources()
      epoch.current++
      buffering.current = false
      stalled.current = 0
      scheduled.current.clear()
      only.current = line
      floor.current = fromMs
      void audio.resume()
      origin.current = audio.currentTime + LEAD_S - fromMs / 1000
      until.current = line ? line.startMs + (line.audio ? line.durationMs : 0) : null
      fill()
    },
    [fill, stopSources],
  )
  reschedule.current = schedule

  /**
   * Call first thing in a click or key handler, before any await: Safari only
   * lets audio start from inside one. Playing a moment of silence there is
   * what convinces it.
   */
  const unlock = useCallback(() => {
    // iOS/iPadOS Safari 17+: without this, Web Audio is muted by the
    // ring/silent switch (as in the trainer's scenePlayer).
    if ('audioSession' in navigator) (navigator as { audioSession: { type: string } }).audioSession.type = 'playback'
    ctx.current ??= new AudioContext()
    const audio = ctx.current
    if (audio.state !== 'running') void audio.resume()
    const silence = audio.createBufferSource()
    silence.buffer = audio.createBuffer(1, 1, audio.sampleRate)
    silence.connect(audio.destination)
    silence.start()
  }, [])

  const load = useCallback(
    async (sceneId: number) => {
      const token = run.current
      ctx.current ??= new AudioContext()

      // Always refetch: lines and takes may have changed since the last play.
      const res = await fetch(adminScenes.playback({ productionId, id: sceneId }).url, {
        headers: { Accept: 'application/json' },
      })
      if (token !== run.current) return null
      if (!res.ok) {
        stop()
        return null
      }
      const { scene, audioUrls } = (await res.json()) as { scene: Scene; audioUrls: Record<string, string> }
      const timeline = planScene(scene, { mode: 'listen', myCharacterIds: new Set() })
      if (token !== run.current) return null
      stale.current = false
      unplayable.current.clear()
      playback.current = { scene, timeline, audioUrls }
      return playback.current
    },
    [productionId, stop],
  )

  /** Starts at a step (once its audio is in): one line in line mode, the rest of the scene otherwise. */
  const startAt = useCallback(
    async (step: Step) => {
      const p = playback.current
      const url = p && urlOf(p, step)
      if (url && !buffers.current.has(url)) {
        const token = run.current
        show(step, { status: 'loading', finished: false })
        await download(url)
        if (token !== run.current) return
      }
      schedule(step.startMs, modeRef.current === 'line' ? step : null)
      show(step, { status: 'playing', finished: false })
    },
    [download, schedule, show],
  )

  /** Plays a scene from the top, or from a line. Call from a click or key: browsers only start audio on a gesture. */
  const play = useCallback(
    async (sceneId: number, fromLineId?: number) => {
      unlock()
      run.current++
      stopSources()
      setState({ sceneId, status: 'loading', current: null, finished: false })
      const p = await load(sceneId)
      if (!p) return
      const steps = p.timeline.steps
      const from =
        (fromLineId && steps.find((s) => s.lineId === String(fromLineId))) ||
        steps.find((s) => s.kind !== 'direction') ||
        steps[0]
      if (!from) return stop()
      prefetch(p, from)
      await startAt(from)
    },
    [load, prefetch, startAt, stop, stopSources, unlock],
  )

  /** The loaded scene, reloaded first if it changed, with the current step looked up again. */
  const fresh = useCallback(async () => {
    const p = playback.current
    if (!p || state.sceneId === null) return null
    if (!stale.current) return { p, step: current.current }
    const lineId = current.current?.lineId
    setState((s) => ({ ...s, status: 'loading' }))
    const reloaded = await load(state.sceneId)
    if (!reloaded) return null
    const step = reloaded.timeline.steps.find((s) => s.lineId === lineId) ?? null
    if (step) prefetch(reloaded, step)
    return { p: reloaded, step }
  }, [load, prefetch, state.sceneId])

  const pause = useCallback(() => {
    if (state.status !== 'playing') return
    pausedAt.current = buffering.current ? floor.current : position()
    epoch.current++
    stopSources()
    setState((s) => ({ ...s, status: 'paused' }))
  }, [position, state.status, stopSources])

  /** Jumps `by` spoken lines from the current one (directions are skipped) and plays it. */
  const step = useCallback(
    async (by: number) => {
      unlock()
      const f = await fresh()
      if (!f) return
      const spoken = f.p.timeline.steps.filter((s) => isSpoken(f.p.scene.lines[s.index]))
      const index = f.step ? spoken.findIndex((s) => s.lineId === f.step?.lineId) : -1
      const target = spoken[index === -1 && by < 0 ? 0 : index + by]
      if (target) await startAt(target)
      else if (f.step) show(f.step, { status: 'paused', finished: true })
    },
    [fresh, show, startAt, unlock],
  )

  /** Plays the current line again from its start. */
  const replay = useCallback(async () => {
    unlock()
    const f = await fresh()
    if (f?.step) await startAt(f.step)
  }, [fresh, startAt, unlock])

  const resume = useCallback(async () => {
    unlock()
    if (state.finished) return step(1)
    // An edit while paused may have changed this line's audio: hear it from the top.
    if (stale.current) return replay()
    schedule(pausedAt.current, modeRef.current === 'line' ? current.current : null)
    setState((s) => ({ ...s, status: 'playing' }))
  }, [replay, schedule, state.finished, step, unlock])

  const togglePause = useCallback(
    () => (state.status === 'playing' ? pause() : state.status === 'paused' ? void resume() : undefined),
    [pause, resume, state.status],
  )

  const setMode = useCallback(
    (next: PlayMode) => {
      modeRef.current = next
      setModeState(next)
      try {
        localStorage.setItem(MODE_KEY, next)
      } catch {
        // private window: just not remembered
      }
      // Switching mid-line keeps the line playing; only what follows changes.
      if (state.status === 'playing') schedule(position(), next === 'line' ? current.current : null)
    },
    [position, schedule, state.status],
  )

  /** The scene was edited or got new audio: pick that up before playing on. */
  const invalidate = useCallback(() => {
    stale.current = true
  }, [])

  // Follow the clock: which line is playing; wait after the line (line
  // mode) or stop at the end of the scene.
  useEffect(() => {
    if (state.status !== 'playing') return
    const id = setInterval(() => {
      const p = playback.current
      if (!p || !ctx.current) return
      // The browser stopped the clock (Safari refusing audio outside a
      // press): show paused, so the next press resumes from a gesture.
      stalled.current = ctx.current.state === 'running' ? 0 : stalled.current + 1
      if (stalled.current > 10) {
        console.warn('Scene player: audio blocked by the browser', ctx.current.state)
        pausedAt.current = Math.max(position(), floor.current)
        epoch.current++
        stopSources()
        setState((s) => ({ ...s, status: 'paused' }))
        return
      }
      fill()
      if (buffering.current) return
      const at = position()
      if (until.current !== null) {
        if (at >= until.current) {
          stopSources()
          pausedAt.current = until.current
          setState((s) => ({ ...s, status: 'paused', finished: true }))
        }
        return
      }
      if (at > p.timeline.totalMs + 500) {
        if (current.current) show(current.current, { status: 'paused', finished: true })
        return
      }
      const now = stepAt(p.timeline, at)
      if (now && now.lineId !== current.current?.lineId) show(now, {})
    }, 50)
    return () => clearInterval(id)
  }, [fill, position, state.status, show, stopSources])

  useEffect(
    () => () => {
      run.current++
      stopSources()
      void ctx.current?.close()
    },
    [stopSources],
  )

  return { ...state, mode, setMode, play, pause, togglePause, step, replay, stop, invalidate }
}

function urlOf(p: Playback, step: Step): string | undefined {
  return step.audio ? p.audioUrls[step.audio.file] : undefined
}
